/**
 * AccessDeniedPanel — reusable denial panel for scope-gated content.
 *
 * Rendered by ScopeGate when the current operator lacks the required scope(s)
 * for a page. Designed to be informative and actionable: the operator sees
 * exactly which scope they need and is told to contact an administrator.
 *
 * Visual treatment uses EmptyState so the layout is consistent with other
 * "no content" states across the app.
 *
 * Task 3552c.
 */

import { Lock } from 'lucide-react'
import { EmptyState } from '@/shared/components/EmptyState'

export interface AccessDeniedPanelProps {
  /** The scope(s) required to access this content. */
  required: string | string[]
  /** Optional context hint, e.g. "to view signing keys". */
  context?: string
}

/**
 * Renders a centred panel explaining which scope(s) the operator needs to
 * access this page, and directing them to contact an administrator.
 */
export function AccessDeniedPanel({ required, context }: AccessDeniedPanelProps) {
  const scopes = Array.isArray(required) ? required : [required]
  const scopeList = scopes.join(', ')
  const contextSuffix = context ? ` ${context}` : ''

  return (
    <EmptyState
      variant="error"
      icon={<Lock />}
      title="Access denied"
      description={
        <>
          You need the following scope{scopes.length > 1 ? 's' : ''}{contextSuffix}:{' '}
          <code style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>{scopeList}</code>.
          {' '}Contact an administrator to request access.
        </>
      }
    />
  )
}
